import React, { useMemo, useCallback, forwardRef } from 'react';
import { MultiSelectInput, type MultiSelectOption } from './MultiSelectInput';
import { cn } from '../../utils/cn';
import { HEALTH_CONDITION_OPTIONS } from '../../constants/healthConditions';
import type { HealthConditionOption } from '../self-referral/types';

interface HealthConditionsSelectorProps {
  selectedConditions: string[];
  onConditionsChange: (conditions: string[]) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * HealthConditionsSelector Component
 *
 * Multi-select interface for health conditions using the MultiSelectInput component.
 * Uses health condition data from constants/healthConditions.ts and follows BiteWise accessibility and design patterns.
 */
export const HealthConditionsSelector = forwardRef<HTMLDivElement, HealthConditionsSelectorProps>(({
  selectedConditions,
  onConditionsChange,
  error,
  disabled = false,
  className,
}, ref) => {
  // Convert HEALTH_CONDITION_OPTIONS to MultiSelectOption format
  const healthConditionOptions = useMemo((): MultiSelectOption[] => {
      return HEALTH_CONDITION_OPTIONS
        .map((condition: HealthConditionOption) => ({
          value: condition.id,
          label: condition.label,
          disabled: false,
          // Add description as additional metadata if needed
          description: condition.description
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }, []);

  // Handle selection change from MultiSelectInput
  const handleSelectionChange = useCallback((selectedValues: string[]) => {
    onConditionsChange(selectedValues);
  }, [onConditionsChange]);

  return (
    <div ref={ref} className={cn(className)}>
      <MultiSelectInput
        options={healthConditionOptions}
        value={selectedConditions}
        onChange={handleSelectionChange}
        placeholder="Select health conditions or goals..."
        dialogTitle="Select Health Conditions"
        searchPlaceholder="Search health conditions..."
        searchable={true}
        maxVisibleCapsules={3}
        state={error ? 'error' : 'default'}
        error={error}
        disabled={disabled}
        helperText="Select all conditions that apply. This helps us match you with a dietitian who specializes in your needs."
        fullWidth
        containerClassName="space-y-1"
        suppressMessage={true}
      />
    </div>
  );
});

HealthConditionsSelector.displayName = 'HealthConditionsSelector';

export default HealthConditionsSelector;